import { useState, useEffect, useRef, useContext } from "react";
import { ATTACH_BASE, THUMB_BASE, LOGO_BASE, API, C } from "./utils";
import CommentsPanel from "./CommentsPanel";
import { ThemeCtx } from "./AppShell";
import { ImageStrip, AdminBar } from "./DocDetailTab";
import { CaptureContent } from "./CaptureScreen";

const openFiltered = (subId, params) => {
  const base = window.location.origin + window.location.pathname;
  const qs = new URLSearchParams({ filter:"1", sub:subId||"", ...params }).toString();
  window.open(`${base}?${qs}`, "_blank", "noopener,noreferrer");
};

// ─── helpers ──────────────────────────────────────────────────────────────────
const isImage   = p => /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(p || "");
const fileName  = p => (p || "").split("/").pop();
const attachUrl = p => ATTACH_BASE + p;
const thumbUrl  = p => THUMB_BASE  + fileName(p);

const PHASE_CONFIG = {
  "Approved":  { color:"#059669", bg:"#D1FAE5" },
  "Phase III": { color:"#1D4ED8", bg:"#DBEAFE" },
  "Phase II":  { color:"#7C3AED", bg:"#EDE9FE" },
  "Phase I":   { color:"#0891B2", bg:"#CFFAFE" },
};
const phaseCfg = p => PHASE_CONFIG[p] || { color:"#6B7280", bg:"#F3F4F6" };

// ─── Logo color extractor (matches AppShell.useLogoColors exactly) ────────────
function useLogoColors(logoUrl) {
  const [palette, setPalette] = useState(null);
  const cache = useRef({});
  useEffect(() => {
    if (!logoUrl) { setPalette(null); return; }
    if (cache.current[logoUrl]) { setPalette(cache.current[logoUrl]); return; }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = 64; canvas.height = 64;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, 64, 64);
        const data = ctx.getImageData(0, 0, 64, 64).data;
        const buckets = {};
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i], g = data[i+1], b = data[i+2], a = data[i+3];
          if (a < 80) continue;
          const max = Math.max(r,g,b), min = Math.min(r,g,b);
          const l = (max+min)/2/255;
          const s = max===min ? 0 : (max-min)/(255*(1-Math.abs(2*l-1)));
          if (l > 0.92 || l < 0.08 || s < 0.18) continue;
          let h = 0;
          if (max !== min) {
            if (max===r) h=((g-b)/(max-min)+6)%6;
            else if (max===g) h=(b-r)/(max-min)+2;
            else h=(r-g)/(max-min)+4;
          }
          const hb = Math.round(h*60/15)*15;
          buckets[hb] = (buckets[hb]||0)+1;
        }
        const sorted = Object.entries(buckets).sort((a,b)=>b[1]-a[1]);
        if (!sorted.length) { setPalette(null); return; }
        const dominant = parseInt(sorted[0][0]);
        const hToRgb = (h,s,l) => {
          const c2=(1-Math.abs(2*l-1))*s, x=c2*(1-Math.abs((h/60)%2-1)), m=l-c2/2;
          let r2=0,g2=0,b2=0;
          if(h<60){r2=c2;g2=x;}else if(h<120){r2=x;g2=c2;}
          else if(h<180){g2=c2;b2=x;}else if(h<240){g2=x;b2=c2;}
          else if(h<300){r2=x;b2=c2;}else{r2=c2;b2=x;}
          const toH=v=>Math.round((v+m)*255).toString(16).padStart(2,"0");
          return `#${toH(r2)}${toH(g2)}${toH(b2)}`;
        };
        const p = {
          primary:     hToRgb(dominant,0.65,0.38),
          accent:      hToRgb(dominant,0.70,0.45),
          accentLight: hToRgb(dominant,0.50,0.92),
          sidebar:     `linear-gradient(180deg,${hToRgb(dominant,0.55,0.22)} 0%,${hToRgb(dominant,0.45,0.18)} 100%)`,
        };
        cache.current[logoUrl] = p;
        setPalette(p);
      } catch { setPalette(null); }
    };
    img.onerror = () => setPalette(null);
    img.src = logoUrl;
  }, [logoUrl]);
  return palette;
}

// ─── Spinner ──────────────────────────────────────────────────────────────────
function Spin({ primary }) {
  return (
    <div style={{ minHeight:"100vh",display:"flex",alignItems:"center",
      justifyContent:"center",background:"#F8FAFC" }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ width:"28px",height:"28px",margin:"0 auto 10px",
          border:"3px solid #E5E7EB",borderTopColor:primary,
          borderRadius:"50%",animation:"spin 0.8s linear infinite" }}/>
        <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
        <div style={{ fontSize:"13px",color:"#9CA3AF" }}>Loading…</div>
      </div>
    </div>
  );
}

// ─── FilePill ─────────────────────────────────────────────────────────────────
function FilePill({ filepath }) {
  const [hov,setHov] = useState(false);
  const name = fileName(filepath).replace(/_\d{2}_\d{2}_\d{4}_\d{2}_\d{2}\./,".");
  const ext  = name.split(".").pop().toUpperCase();
  const isImg= isImage(filepath);
  const ec   = isImg?"#059669":ext==="PDF"?"#DC2626":ext==="PPTX"||ext==="PPT"?"#D97706":
               ext==="DOCX"||ext==="DOC"?"#2563EB":"#6B7280";
  const em   = isImg?"🖼":ext==="PDF"?"📄":ext==="PPTX"||ext==="PPT"?"📊":
               ext==="DOCX"?"📝":"📎";
  return (
    <div onClick={()=>window.open(attachUrl(filepath),"_blank")}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{ display:"inline-flex",alignItems:"center",gap:"4px",
        padding:"3px 9px",borderRadius:"7px",cursor:"pointer",
        background:hov?`${ec}10`:"#fff",
        border:`1px solid ${hov?ec:ec+"44"}`,
        fontSize:"11px",color:"#374151",transition:"all 0.12s" }}>
      <span>{em}</span>
      <span style={{ maxWidth:"160px",overflow:"hidden",textOverflow:"ellipsis",
        whiteSpace:"nowrap",fontWeight:"500" }}>{name}</span>
      <span style={{ background:ec,color:"#fff",borderRadius:"4px",
        padding:"0 4px",fontSize:"8px",fontWeight:"800" }}>{ext}</span>
    </div>
  );
}

// ─── Source links ─────────────────────────────────────────────────────────────
function SourceLinks({ html, primary }) {
  if (!html) return null;
  const doc     = new DOMParser().parseFromString(html,"text/html");
  const anchors = Array.from(doc.querySelectorAll("a"));
  const links   = anchors.length>0
    ? anchors.map(a=>{
        const href=a.getAttribute("href")||"";
        if(!href||href.startsWith("blob:")) return null;
        return { href, label:a.textContent.trim()||"Link" };
      }).filter(Boolean)
    : doc.body.textContent.trim()
        .split(/,\s*(?=https?:\/\/)/)
        .map((u,i)=>({ href:u.trim(),label:`Source ${i+1}` }))
        .filter(x=>/^https?:\/\//.test(x.href));
  if (!links.length) return null;
  return (
    <div style={{ display:"flex",flexWrap:"wrap",gap:"5px" }}>
      {links.map((lnk,i)=>(
        <a key={i} href={lnk.href} target="_blank" rel="noopener noreferrer"
          style={{ display:"inline-flex",alignItems:"center",gap:"3px",
            padding:"2px 8px",borderRadius:"5px",fontSize:"11px",fontWeight:"600",
            color:primary,background:`${primary}10`,
            border:`1px solid ${primary}30`,textDecoration:"none" }}>
          🔗 {lnk.label}
        </a>
      ))}
    </div>
  );
}

// ─── Detail row (expandable — matches ProductProfileDetail.DetailRow) ─────────
function DetailRow({ d, index, primary }) {
  const [open,setOpen] = useState(index===0);
  const pc      = phaseCfg(d.PHASENAME);
  const chatbot = Array.isArray(d.CHATBOT)?d.CHATBOT:[];
  const images  = chatbot.filter(a=>isImage(a.FILEPATH));
  const files   = chatbot.filter(a=>!isImage(a.FILEPATH));
  const country = d.REGIONNAME||d.COUNTRYNAME||"";

  return (
    <div style={{ borderBottom:"1px solid #F1F5F9" }}>
      <div onClick={()=>setOpen(o=>!o)}
        style={{ display:"grid",gridTemplateColumns:"92px 1fr 120px 28px",
          gap:"6px",alignItems:"center",padding:"9px 14px",cursor:"pointer",
          background:open?`${pc.color}08`:"transparent",transition:"background 0.1s" }}>
        <span style={{ fontSize:"10px",fontWeight:"700",padding:"2px 7px",
          borderRadius:"5px",color:pc.color,background:pc.bg,
          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
          display:"inline-block" }}>{d.PHASENAME||"—"}</span>
        <span style={{ fontSize:"12px",fontWeight:"600",color:"#111827",
          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
          {d.INDICATIONNAME||"—"}
        </span>
        <span style={{ fontSize:"11px",color:primary,
          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
          {d.ACRONYMS||""}
        </span>
        <span style={{ fontSize:"11px",color:"#9CA3AF",textAlign:"right",
          transform:open?"rotate(180deg)":"none",transition:"transform 0.18s",
          display:"inline-block" }}>▼</span>
      </div>

      {open&&(
        <div style={{ padding:"12px 14px 14px",background:"#F8FAFC",
          borderTop:"1px solid #F1F5F9" }}>
          <div>
              {d.TITLE&&<div style={{ fontSize:"12px",fontWeight:"600",color:"#111827",
                lineHeight:1.4,marginBottom:"10px" }}>{d.TITLE}</div>}
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px" }}>
                {[
                  { label:"Anticipated launch", val:d.LAUNCHDATE },
                  { label:"Market",             val:country },
                  { label:"Launch assumptions", val:d.LAUNCHASSUMPTIONS },
                ].filter(r=>r.val).map((r,i)=>(
                  <div key={i}>
                    <div style={{ fontSize:"10px",color:"#9CA3AF",marginBottom:"1px" }}>{r.label}</div>
                    <div style={{ fontSize:"12px",color:"#374151",fontWeight:"500" }}>{r.val}</div>
                  </div>
                ))}
              </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function ProductDetailPage({ session }) {
  const [data,     setData]    = useState(null);
  const [loading,  setLoading] = useState(true);
  const [error,    setError]   = useState(null);
  const [tab,      setTab]     = useState("attachments");
  const [editDoc,  setEditDoc] = useState(null);
  const [editLoad, setEditLoad]= useState(false);

  const params       = new URLSearchParams(window.location.search);
  const documentId   = params.get("doc");
  const subprojectId = params.get("sub");
  const userid       = session?.user?.USERID;
  const authCode     = session?.user?.AUTH_CODE;

  // Use ThemeCtx from AppShell — same theme as the rest of the app
  const ctx      = useContext(ThemeCtx) || {};
  const theme    = ctx.theme || {};
  const primary  = theme.primary  || C.primary;
  const tileGrad = theme.tileGrad || `linear-gradient(135deg,${primary} 0%,${C.dark} 100%)`;

  useEffect(() => {
    if (!documentId || !subprojectId || !userid) {
      setError("Missing required parameters.");
      setLoading(false);
      return;
    }
    fetch(`${API}/product_detail?documentid=${documentId}&subprojectid=${subprojectId}&userid=${userid}`,
      { headers:{ "Auth-Code": authCode||"" } })
      .then(r=>r.json())
      .then(res => {
        if (res.STATUS==="1" && res.DATA?.length) { setData(res.DATA[0]); }
        else setError(res.MESSAGE||"No data found.");
      })
      .catch(()=>setError("Failed to load."))
      .finally(()=>setLoading(false));
  }, [documentId, subprojectId, userid]);

  if (loading) return <Spin primary={primary}/>;
  if (error)   return (
    <div style={{ minHeight:"100vh",display:"flex",alignItems:"center",
      justifyContent:"center",background:"#F8FAFC" }}>
      <div style={{ fontSize:"14px",color:"#EF4444" }}>{error}</div>
    </div>
  );
  if (!data) return null;

  const isAdmin = session?.subproject?.ROLE === 1;

  const fetchAndEdit = async () => {
    setEditLoad(true);
    try {
      const r = await fetch(
        `${API}/advance_filter?userid=${userid}&subprojectid=${subprojectId}&documentid=${documentId}&doctypeid=${data.DOCTYPEID||98}`,
        { headers:{"Auth-Code":authCode||""} }
      );
      const res = await r.json();
      if((res.STATUS==="1"||res.STATUS===1)&&res.DATA?.length>0) setEditDoc(res.DATA[0]);
    } catch{}
    setEditLoad(false);
  };

  if(editDoc) return(
    <div style={{height:"100%",overflow:"auto",display:"flex",flexDirection:"column"}}>
      <div style={{padding:"10px 16px",borderBottom:"1px solid #E5E7EB",flexShrink:0,
        display:"flex",alignItems:"center",gap:"10px",background:"#fff"}}>
        <button onClick={()=>setEditDoc(null)}
          style={{padding:"5px 14px",borderRadius:"20px",border:"1px solid #E5E7EB",
            background:"#F9FAFB",color:"#6B7280",fontSize:"12px",fontWeight:"600",cursor:"pointer"}}>
          ← Back
        </button>
        <span style={{fontSize:"14px",fontWeight:"700",color:"#111"}}>{data.TITLE||"Edit"}</span>
      </div>
      <div style={{flex:1,overflow:"auto"}}>
        <CaptureContent session={session} editDoc={editDoc} onEditDone={()=>setEditDoc(null)}/>
      </div>
    </div>
  );

  const chatbot  = Array.isArray(data.CHATBOT)     ? data.CHATBOT     : [];
  const images   = chatbot.filter(a=>isImage(a.FILEPATH));
  const files    = chatbot.filter(a=>!isImage(a.FILEPATH));
  const products = Array.isArray(data.PRODUCTS)    ? data.PRODUCTS    : [];
  const sponsors = Array.isArray(data.COMPANY)     ? data.COMPANY     : [];
  const inds     = Array.isArray(data.INDICATIONS) ? data.INDICATIONS : [];

  // Build detail rows — one per indication (this doc IS doctypeid=98)
  const detailRows = inds.map(ind => ({ ...data, INDICATIONNAME: ind.INDICATIONNAME }));

  const primaryBrand = products.find(p=>p.PRIMARY===1) || products[0] || {};

  return (
    <div style={{ display:"flex",flexDirection:"column",height:"100%",
      overflow:"hidden",background:"#F8FAFC" }}>

      {/* ── AdminBar — activate/deactivate + edit ── */}
      {/* ── Hero ── */}
      <div style={{ flexShrink:0,padding:"16px 20px",
        background:`linear-gradient(135deg,${primary}F0,${primary}88)`,
        position:"relative",overflow:"hidden" }}>
        <div style={{ position:"absolute",right:"-20px",top:"-20px",width:"130px",
          height:"130px",borderRadius:"50%",background:"rgba(255,255,255,0.05)" }}/>
        <div style={{ display:"flex",gap:"12px",alignItems:"flex-start",position:"relative" }}>
          {/* Company logo */}
          {sponsors[0]?.COMPANY_LOGO&&(
            <div style={{ width:"44px",height:"44px",borderRadius:"10px",flexShrink:0,
              background:"rgba(255,255,255,0.18)",border:"1.5px solid rgba(255,255,255,0.3)",
              overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center" }}>
              <img src={LOGO_BASE+sponsors[0].COMPANY_LOGO} alt=""
                onError={e=>e.target.style.display="none"}
                style={{ width:"80%",height:"80%",objectFit:"contain" }}/>
            </div>
          )}
          <div style={{ flex:1,minWidth:0 }}>
            {/* Brand name + Edit */}
            <div style={{ marginBottom:"5px" }}>
              <div style={{ fontSize:"18px",fontWeight:"800",color:"#fff",lineHeight:1.2 }}>
                {primaryBrand.BRANDNAME||data.TITLE}
              </div>
            </div>
            {/* Clickable pills */}
            <div style={{ display:"flex",gap:"5px",flexWrap:"wrap",marginBottom:"6px" }}>
              {products.filter(p=>String(p.BRANDID)!=="0").map(p=>(
                <span key={p.BRANDID}
                  onClick={()=>openFiltered(subprojectId,{brandid:p.BRANDID})}
                  style={{ fontSize:"10px",fontWeight:"600",cursor:"pointer",
                    padding:"2px 9px",borderRadius:"20px",
                    background:p.PRIMARY===1?"#FCD34D":"rgba(252,211,77,0.15)",
                    color:p.PRIMARY===1?"#78350F":"rgba(252,211,77,0.85)",
                    border:`1px solid ${p.PRIMARY===1?"#FCD34D":"rgba(252,211,77,0.3)"}` }}>
                  {p.BRANDNAME}
                </span>
              ))}
              {sponsors.filter(s=>String(s.SPONSORID)!=="0").map(s=>(
                <span key={s.SPONSORID}
                  onClick={()=>openFiltered(subprojectId,{sponsorid:s.SPONSORID})}
                  style={{ fontSize:"11px",fontWeight:"600",cursor:"pointer",
                    padding:"2px 9px",borderRadius:"20px",
                    background:"rgba(255,255,255,0.92)",color:primary }}>
                  {s.SPONSORNAME}
                </span>
              ))}
              {inds.filter(i=>String(i.INDICATIONID)!=="0").map(i=>(
                <span key={i.INDICATIONID}
                  onClick={()=>openFiltered(subprojectId,{indicationid:i.INDICATIONID})}
                  style={{ fontSize:"10px",cursor:"pointer",
                    padding:"2px 9px",borderRadius:"20px",
                    background:"rgba(167,243,208,0.9)",color:"#065F46" }}>
                  {i.INDICATIONNAME}
                </span>
              ))}
              {data.ACRONYMS&&(
                <span style={{ fontSize:"10px",fontWeight:"800",color:"#FCD34D",
                  background:"rgba(0,0,0,0.2)",borderRadius:"5px",padding:"1px 8px" }}>
                  {data.ACRONYMS}
                </span>
              )}
              {data.PHASENAME&&(
                <span style={{ fontSize:"10px",fontWeight:"700",padding:"2px 8px",
                  borderRadius:"5px",background:"rgba(255,255,255,0.2)",color:"#fff" }}>
                  {data.PHASENAME}
                </span>
              )}
            </div>
            {/* Keywords */}
            {data.KEYWORDS&&(
              <div style={{ display:"flex",flexWrap:"wrap",gap:"4px" }}>
                {data.KEYWORDS.split(/[;,]/).map(k=>k.trim()).filter(Boolean).map((k,i)=>(
                  <span key={i} style={{ fontSize:"9px",fontWeight:"600",
                    color:"rgba(255,255,255,0.85)",background:"rgba(255,255,255,0.15)",
                    borderRadius:"20px",padding:"1px 7px" }}>{k}</span>
                ))}
              </div>
            )}
          </div>
          {/* Compact image — same as DocDetailTab hero */}
          {images.length>0&&(
            <div style={{ width:"90px",flexShrink:0,borderRadius:"10px",overflow:"hidden",
              border:"2px solid rgba(255,255,255,0.25)" }}>
              <ImageStrip images={images} compact={true}/>
            </div>
          )}
        </div>
      </div>

      {/* ── AdminBar — Save/Like/Share/Edit ── */}
      <AdminBar doc={data} user={session?.user} activeSub={session?.subproject}
        tPrimary={primary}
        onEdit={isAdmin ? fetchAndEdit : undefined}/>
      {editLoad&&(
        <div style={{padding:"8px 16px",fontSize:"12px",color:"#6B7280",
          background:"#F8FAFC",borderBottom:"1px solid #E5E7EB"}}>
          Loading document for edit…
        </div>
      )}

      {/* ── Body ── */}
      <div style={{ flex:1,display:"flex",overflow:"hidden",minHeight:0 }}>

        {/* LEFT: indications table + overview + source */}
        <div style={{ flex:1,overflowY:"auto",padding:"16px" }}>

          {/* Indications & details */}
          {detailRows.length>0&&(
            <div style={{ background:"#fff",borderRadius:"12px",
              border:"1px solid #E5E7EB",overflow:"hidden",marginBottom:"14px",
              boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
              <div style={{ padding:"9px 14px",borderBottom:"1px solid #F1F5F9",
                display:"flex",alignItems:"center",background:"#F8FAFC" }}>
                <span style={{ fontSize:"12px",fontWeight:"700",color:"#374151" }}>
                  Indications & details
                </span>
              </div>
              <div style={{ display:"grid",gridTemplateColumns:"92px 1fr 120px 28px",
                gap:"6px",padding:"5px 14px",background:"#FAFAFA",
                borderBottom:"1px solid #F1F5F9",fontSize:"10px",
                fontWeight:"700",color:"#9CA3AF",textTransform:"uppercase",letterSpacing:"0.5px" }}>
                <span>Phase</span><span>Indication</span><span>Acronym</span><span/>
              </div>
              {detailRows.map((d,i)=>(
                <DetailRow key={i} d={d} index={i} primary={primary}/>
              ))}
            </div>
          )}

          {/* Overview */}
          {data.DESCRIPTION&&(
            <div style={{ background:"#fff",borderRadius:"12px",
              border:"1px solid #E5E7EB",padding:"14px 16px",
              boxShadow:"0 1px 4px rgba(0,0,0,0.04)",marginBottom:"14px" }}>
              <div style={{ fontSize:"11px",fontWeight:"700",color:"#9CA3AF",
                textTransform:"uppercase",letterSpacing:"0.6px",marginBottom:"8px" }}>
                Overview
              </div>
              <div style={{ fontSize:"13px",color:"#374151",lineHeight:1.75 }}
                dangerouslySetInnerHTML={{ __html:data.DESCRIPTION }}/>
            </div>
          )}

          {/* Source — inline below content */}
          {data.LINK&&(
            <div style={{ display:"flex",alignItems:"flex-start",gap:"8px",
              fontSize:"12px",padding:"8px 0",marginBottom:"6px" }}>
              <span style={{ color:"#9CA3AF",fontWeight:"600",minWidth:"52px",flexShrink:0 }}>Source</span>
              <SourceLinks html={data.LINK} primary={primary}/>
            </div>
          )}


        </div>

        {/* RIGHT: image + meta + tabs */}
        <div style={{ width:"280px",flexShrink:0,display:"flex",flexDirection:"column",
          overflow:"hidden",background:"#F8FAFC",borderLeft:"1px solid #E5E7EB" }}>

          {/* Tabs — Attachments, Comments, Polls */}
          <div style={{ flex:1,display:"flex",flexDirection:"column",overflow:"hidden" }}>
            <div style={{ display:"flex",borderBottom:"1px solid #E5E7EB",
              flexShrink:0,background:"#fff" }}>
              {["attachments","comments","polls"].map(t=>(
                <button key={t} onClick={()=>setTab(t)}
                  style={{ padding:"7px 12px",border:"none",background:"none",cursor:"pointer",
                    fontSize:"11px",fontWeight:tab===t?"700":"500",
                    color:tab===t?primary:"#6B7280",
                    borderBottom:`2px solid ${tab===t?primary:"transparent"}`,
                    textTransform:"capitalize",transition:"all 0.15s" }}>
                  {t}
                </button>
              ))}
            </div>
            <div style={{ flex:1,overflowY:"auto" }}>
              {tab==="attachments"&&(
                files.length>0
                  ?<div style={{ padding:"10px 12px",display:"flex",flexWrap:"wrap",gap:"6px" }}>
                    {files.map((f,i)=><FilePill key={i} filepath={f.FILEPATH}/>)}
                  </div>
                  :<div style={{ padding:"2rem",textAlign:"center",
                    color:"#9CA3AF",fontSize:"12px" }}>No attachments</div>
              )}
              {tab==="comments"&&(
                <CommentsPanel user={session?.user} activeSub={session?.subproject} documentId={data?.DOCUMENTID}/>
              )}
              {tab==="polls"&&(
                <CommentsPanel user={session?.user} activeSub={session?.subproject} documentId={data?.DOCUMENTID} pollOnly={true}/>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}